import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { datadogRum } from '@/lib/DatadogService';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { RecaptchaActionName, reportRecaptchaAction } from '@/lib/recaptcha';
import {
  PushAnalyticsEvent,
  createSyntheticPageViewEvent,
  stripTransientQueryParams
} from '@/analytics/analyticsUtils';
import { AnalyticsEventName } from '@/analytics/constants';

// Create a module-level variable that persists across route changes but resets on page refresh
const hasNavigatedBefore = { value: false };

type PageLinks = RootState['page']['pageLinks'];
type SiteLabels = RootState['page']['siteLabels'];

const normalizePath = (url?: string) => {
  if (!url) return undefined;

  try {
    return new URL(url, window.location.origin).pathname;
  } catch {
    return url.split('?')[0];
  }
};

const matchesPath = (pathname: string, url?: string) => normalizePath(url) === pathname;

// Internal search params (_ns, _so, _q, ...) are added and stripped by in-page URL
// replaces (e.g. search removing NEW_SEARCH after resetting state); exclude them so a
// URL normalization is not counted as a new pageview.
const buildPageViewKey = (pathname: string, queryString: string) => {
  const params = new URLSearchParams(queryString);
  [...params.keys()].filter(key => key.startsWith('_')).forEach(key => params.delete(key));
  const normalized = params.toString();
  return normalized ? `${pathname}?${normalized}` : pathname;
};

const getPageViewRecaptchaAction = (
  pathname: string,
  pageLinks: PageLinks,
  siteLabels: SiteLabels
): RecaptchaActionName => {
  if (matchesPath(pathname, siteLabels.cartUrl) || pathname.includes('/cart')) {
    return 'cart_view';
  }

  if (
    matchesPath(pathname, siteLabels.checkoutPageReference?.url) ||
    pathname.includes('/checkout')
  ) {
    return 'checkout';
  }

  if (matchesPath(pathname, pageLinks.Profile) || pathname.includes('/profile')) {
    return 'account_profile';
  }

  if (matchesPath(pathname, pageLinks.Library) || pathname.includes('/library')) {
    return 'library';
  }

  if (matchesPath(pathname, pageLinks.Search) || pathname.includes('/search')) {
    return 'search';
  }

  if (
    pathname.includes('/program') ||
    pathname.includes('/publication') ||
    pathname.includes('/product')
  ) {
    return 'product_detail';
  }

  return 'page_view';
};

const scheduleRecaptchaPageViewAction = (getAction: () => RecaptchaActionName) => {
  let idleCallbackId: number | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let isCancelled = false;

  const reportVirtualPage = () => {
    if (isCancelled) return;

    reportRecaptchaAction(getAction()).catch(err => {
      console.error('Failed to report reCAPTCHA page view action:', err);
    });
  };

  const scheduleReport = () => {
    if ('requestIdleCallback' in window) {
      idleCallbackId = window.requestIdleCallback(reportVirtualPage, { timeout: 3000 });
    } else {
      timeoutId = setTimeout(reportVirtualPage, 1500);
    }
  };

  if (document.readyState === 'complete') {
    scheduleReport();
  } else {
    window.addEventListener('load', scheduleReport, { once: true });
  }

  return () => {
    isCancelled = true;
    window.removeEventListener('load', scheduleReport);

    if (idleCallbackId !== undefined) {
      window.cancelIdleCallback(idleCallbackId);
    }

    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  };
};

export const EnhancedRouteTracker: React.FC = () => {
  const location = useLocation();
  const navigationStartTimeRef = useRef(0);
  const previousUriRef = useRef<string | null>(null);
  const lastTrackedPageKeyRef = useRef<string | null>(null);

  const DEBUG_TRACKING = import.meta.env.DEV || import.meta.env.VITE_DEBUG_TRACKING === 'true';

  const siteLabels = useSelector((state: RootState) => state.page.siteLabels);
  const pageLinks = useSelector((state: RootState) => state.page.pageLinks);

  // Latest store values, readable inside the tracking effect without re-triggering it
  // when they resolve asynchronously on the same page.
  const pageLinksRef = useRef(pageLinks);
  const siteLabelsRef = useRef(siteLabels);

  useEffect(() => {
    pageLinksRef.current = pageLinks;
    siteLabelsRef.current = siteLabels;
  }, [pageLinks, siteLabels]);

  useEffect(() => {
    if (!siteLabels) return;

    const container = document.getElementById('embedded-messaging');
    if (container) {
      if ([siteLabels.cartUrl, siteLabels.checkoutPageReference?.url].includes(location.pathname)) {
        container.style.display = 'none';
      } else {
        container.style.display = 'block';
      }
    }
  }, [location.pathname, siteLabels]);

  useEffect(() => {
    const pageName = location.pathname;
    const uri = `${window.location.origin}${pageName}`;
    const queryString = location.search;
    const refUri = previousUriRef.current || '';

    // Same page after ignoring internal params (e.g. search stripping _ns via replace):
    // skip all tracking so no duplicate pageview, Datadog action or reCAPTCHA report.
    const pageViewKey = buildPageViewKey(pageName, queryString);
    if (lastTrackedPageKeyRef.current === pageViewKey) return;

    const navigationType = hasNavigatedBefore.value ? 'spa' : 'pageLoad';

    // Mark that we've navigated at least once
    hasNavigatedBefore.value = true;

    // Generate a consistent operationId for this page view that will be used throughout
    const operationId = crypto.randomUUID().replace(/-/g, '');
    const start = performance.now();
    navigationStartTimeRef.current = start;

    // Get accurate page load timing for full page loads
    let pageLoadDuration = 0;

    if (navigationType === 'pageLoad' && window.performance) {
      const navEntries = performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navTiming = navEntries[0] as PerformanceNavigationTiming;
        pageLoadDuration = navTiming.loadEventEnd > 0 ? navTiming.loadEventEnd : performance.now();
      }
    }

    if (DEBUG_TRACKING) {
      // eslint-disable-next-line no-console
      console.log('🔄 [RouteTracker] Route change detected:', {
        path: pageName,
        url: window.location.href,
        queryString,
        refUri,
        navigationType,
        timestamp: new Date().toISOString(),
        operationId
      });
    }

    let cleanupRecaptchaPageViewAction: (() => void) | undefined;
    let maxWaitTimeout: number | undefined;
    let totalExperienceTimeout: number | undefined;
    let loadExperienceTimeout: number | undefined;
    let handleWindowLoad: (() => void) | undefined;

    const animationFrameId = requestAnimationFrame(() => {
      const initialRenderDuration = performance.now() - start;

      if (DEBUG_TRACKING) {
        // eslint-disable-next-line no-console
        console.log('⏱️ [RouteTracker] Initial render complete:', {
          path: location.pathname,
          renderTime: `${Math.round(initialRenderDuration)}ms`,
          ...(navigationType === 'pageLoad' && pageLoadDuration > 0
            ? {
                pageLoadTime: `${Math.round(pageLoadDuration)}ms`
              }
            : {}),
          operationId,
          queryString,
          refUri,
          navigationType,
          timestamp: new Date().toISOString()
        });
      }

      cleanupRecaptchaPageViewAction = scheduleRecaptchaPageViewAction(() =>
        getPageViewRecaptchaAction(pageName, pageLinksRef.current, siteLabelsRef.current)
      );

      lastTrackedPageKeyRef.current = pageViewKey;

      // Synthetic pageview mirroring GA4. Single push to window.dataLayer: GTM consumes it
      // for GA4, and the loadExternals.js dataLayer mirror clones it into eventHubDataLayer
      // (a direct eventHub push here would duplicate it). operation_id links it to the
      // Datadog InitialRender/PageExperienceComplete actions and is the pageview dedup key.
      const virtualPageView = createSyntheticPageViewEvent({
        uri,
        refUri,
        queryString,
        navigationType,
        operationId,
        renderTimeMs: initialRenderDuration,
        pageLoadTimeMs: navigationType === 'pageLoad' ? pageLoadDuration : undefined
      });

      PushAnalyticsEvent(AnalyticsEventName.VirtualPageView, virtualPageView);

      // Track with Datadog
      datadogRum.addAction('InitialRender', {
        renderTime: `${Math.round(initialRenderDuration)}ms`,
        ...(navigationType === 'pageLoad' && pageLoadDuration > 0
          ? {
              pageLoadTime: `${Math.round(pageLoadDuration)}ms`
            }
          : {}),
        operationId,
        queryString,
        refUri,
        navigationType
      });

      // Full URL including query string (minus transient params like _ns): feeds
      // page_referrer of the next pageview
      previousUriRef.current = `${uri}${stripTransientQueryParams(queryString)}`;

      // Track a broader "total experience" if needed
      let skipTotalExperienceTracked = false;

      const trackTotalPageExperience = () => {
        if (skipTotalExperienceTracked) return;
        skipTotalExperienceTracked = true;

        // Calculate total experience time correctly based on navigation type
        const totalExperienceTime =
          navigationType === 'spa'
            ? performance.now() - navigationStartTimeRef.current
            : pageLoadDuration + (performance.now() - start);

        datadogRum.addAction('PageExperienceComplete', {
          path: location.pathname,
          uri,
          duration: totalExperienceTime,
          measurementType: 'totalExperience',
          customOperationId: operationId,
          queryString,
          refUri,
          navigationType
        });
      };

      // Safety net: max wait for total experience
      maxWaitTimeout = window.setTimeout(() => {
        if (!skipTotalExperienceTracked) {
          if (DEBUG_TRACKING) {
            // eslint-disable-next-line no-console
            console.log('⏰ [RouteTracker] Maximum wait time reached for total experience');
          }
          trackTotalPageExperience();
        }
      }, 8000);

      handleWindowLoad = () => {
        loadExperienceTimeout = window.setTimeout(() => {
          if (!skipTotalExperienceTracked) {
            trackTotalPageExperience();
          }
        }, 500);
      };

      window.addEventListener('load', handleWindowLoad, { once: true });

      totalExperienceTimeout = window.setTimeout(() => {
        if (!skipTotalExperienceTracked) {
          trackTotalPageExperience();
        }
      }, 2000);
    });

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      cleanupRecaptchaPageViewAction?.();

      if (maxWaitTimeout !== undefined) {
        window.clearTimeout(maxWaitTimeout);
      }

      if (totalExperienceTimeout !== undefined) {
        window.clearTimeout(totalExperienceTimeout);
      }

      if (loadExperienceTimeout !== undefined) {
        window.clearTimeout(loadExperienceTimeout);
      }

      if (handleWindowLoad) {
        window.removeEventListener('load', handleWindowLoad);
      }
    };
  }, [location.pathname, location.search, DEBUG_TRACKING]);

  return null;
};

export default EnhancedRouteTracker;
